import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Input,
  Typography,
  IconButton,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon, Plus as AddIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { catalogApi, pricingApi, type CatalogProduct } from '../../lib/distributorsApi';
import { useDebounce } from '../../hooks/useDebounce';
import CatalogItemImage from './CatalogItemImage';
import { cartLineUnit } from './CatalogOrderPanel';
import type { ApiCartLine } from './ApiOrderCart';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';

interface Props {
  isSrs?: boolean;
  distributorId: number;
  onAdd: (product: CatalogProduct) => void;
  disabled?: boolean;
  cartLines?: ApiCartLine[];
  /** Height of the scrolling results area. The full-screen browser passes '100%'. */
  resultsHeight?: number | string;
  /** Thumbnail edge length. */
  thumbnailSize?: number;
  autoFocus?: boolean;
  /**
   * Ship-To and branch the order will use. Prices are per customer per branch,
   * so browsing has to quote against the same pair the cart will, or the
   * numbers here would not be the numbers you get after adding.
   */
  shipToNumber?: string;
  branchNumber?: string;
}

/** Live ABC catalog search (debounced, paginated, image thumbnails). */
export default function CatalogProductSearch({
  distributorId,
  onAdd,
  disabled,
  cartLines = [],
  resultsHeight = 420,
  thumbnailSize = 48,
  autoFocus = false,
  shipToNumber,
  branchNumber,
  isSrs,
}: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const debouncedTerm = useDebounce(searchTerm.trim(), 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedTerm, distributorId, branchNumber]);

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['catalog-search', distributorId, branchNumber, debouncedTerm, page],
    queryFn: () => catalogApi.search(distributorId, { q: debouncedTerm, page, page_size: 20, branch: branchNumber }),
    enabled: debouncedTerm.length >= 2 && (!isSrs || !!branchNumber),
    staleTime: 0,
    gcTime: 0,
    retry: (count, error) => count < 5 && ((error as any)?.responseData?.code || (error as any)?.response?.data?.code) === 'catalog_warming',
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 8000),
  });

  const products = data?.results || [];
  const pagination = (data as any)?.pagination || {};
  const totalPages: number = pagination.total_pages || 1;
  const totalItems: number = data?.count ?? products.length;

  // ABC's catalogue search returns no prices — they come from a separate
  // customer- and branch-specific call. Quote the page being shown (20 rows,
  // well under the 50-line API cap) so a price is visible while browsing
  // instead of only after adding to the cart. purpose=estimating is ABC's own
  // term for "looking, not ordering".
  const pricedItems = products.filter((product) => !product.is_dimensional);
  const priceKey = pricedItems.map((product) => product.item_number).join(',');

  const { data: quote, isFetching: pricing, error: pricingError, refetch: refreshPrices } = useQuery({
    queryKey: ['catalog-page-prices', distributorId, shipToNumber || '', branchNumber || '', priceKey],
    queryFn: () =>
      pricingApi.quote(distributorId, {
        ship_to_number: shipToNumber || undefined,
        branch_number: branchNumber || undefined,
        purpose: 'estimating',
        lines: pricedItems.map((product) => ({
          id: product.item_number,
          item_number: product.item_number,
          quantity: 1,
          uom: product.uoms[0]?.code || undefined,
        })),
      }),
    enabled: pricedItems.length > 0 && !!shipToNumber && !!branchNumber,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 5 * 60 * 1000,
    retry: false,
  });

  const priceFor = (product: CatalogProduct) =>
    (quote?.lines || []).find((line: any) => line.id === product.item_number);

  const columns: ResponsiveColumn<CatalogProduct>[] = [
    {
      key: 'image',
      header: '',
      mobileLabel: 'Image',
      width: `${thumbnailSize + 24}px`,
      render: (product) => (
        <CatalogItemImage
          distributorId={distributorId}
          assetId={product.image_asset_id || undefined}
          alt={product.description}
          size={thumbnailSize}
        />
      ),
    },
    {
      key: 'product',
      sortValue: (row) => row.description,
      header: 'Product',
      mobileVariant: 'header',
      render: (product) => (
        <Box>
          <Typography level="body-md" fontWeight="lg">{product.description}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {cartLines.filter(line => line.item_number === product.item_number).map(line =>
              <Chip key={line.lineId} size="sm" variant="soft" color="success">In order: {line.quantity} {cartLineUnit(line)}</Chip>)}
          </Box>
        </Box>
      ),
    },
    {
      key: 'item_number',
      sortValue: (row) => row.item_number,
      header: 'Item #',
      width: '16%',
      render: (product) => (
        <Typography level="body-sm" fontFamily="monospace">
          {product.item_number}
        </Typography>
      ),
    },
    {
      key: 'supplier',
      sortValue: (row) => row.supplier_name,
      header: 'Supplier',
      width: '18%',
      render: (product) => (
        <Typography level="body-sm" color="neutral">
          {product.supplier_name || '—'}
        </Typography>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      width: '14%',
      render: (product) => {
        if (product.is_dimensional) {
          return (
            <Typography level="body-xs" color="neutral">
              Priced by length
            </Typography>
          );
        }
        const line = priceFor(product);
        if (!line) {
          return pricing ? (
            <CircularProgress size="sm" />
          ) : (
            <Typography level="body-xs" color="neutral">
              —
            </Typography>
          );
        }
        if (!line.ok) {
          // Per-line failures are data, not errors: ABC prices what it can.
          return (
            <Typography level="body-xs" color="warning" title={line.status_message || ''}>
              {isSrs && line.status_message?.includes('Please call branch') ? 'Please call branch for product pricing.' : 'Not priced'}
            </Typography>
          );
        }
        const amount = parseFloat(line.unit_price ?? '0') || 0;
        if (amount === 0) {
          // A real ABC answer, not a failure — the branch confirms on receipt.
          return (
            <Typography level="body-xs" color="warning">
              {isSrs ? 'Please call branch for product pricing.' : 'Confirm with branch'}
            </Typography>
          );
        }
        return (
          <Box>
            <Typography level="body-sm" fontWeight="lg">
              ${amount.toFixed(2)}
            </Typography>
            <Typography level="body-xs" color="neutral">
              per {line.uom || product.uoms[0]?.code || 'EA'}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: 'uom',
      header: 'Sold by',
      width: '14%',
      render: (product) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {product.uoms.slice(0, 3).map((uom) => (
            <Chip key={uom.code} size="sm" variant="outlined" color="neutral">
              {uom.code}
            </Chip>
          ))}
          {product.is_dimensional && (
            <Chip size="sm" variant="soft" color="primary">
              Cut to length
            </Chip>
          )}
        </Box>
      ),
    },
  ];

  const fills = resultsHeight === '100%';

  return (
    <Box
      sx={
        fills
          ? { display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }
          : undefined
      }
    >
      <Input
        placeholder="Search the distributor catalog…"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        startDecorator={<SearchIcon />}
        endDecorator={isFetching ? <CircularProgress size="sm" /> : null}
        autoFocus={autoFocus}
        sx={{ mb: 1 }}
      />
      <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
        Search the selected branch’s catalog by description, or paste an item number.
      </Typography>

      {(!shipToNumber || !branchNumber) && <Alert color="warning" sx={{ mb: 1 }}>Select a Ship-To account and branch on the order to see your prices.</Alert>}
      {pricingError && <Alert color="warning" sx={{ mb: 1 }}>Customer pricing could not be loaded. {(pricingError as any)?.responseData?.error || (pricingError as any)?.response?.data?.error || ''}<Button size="sm" variant="plain" onClick={() => void refreshPrices()}>Retry prices</Button></Alert>}
      {debouncedTerm.length < 2 ? (
        <Alert color="neutral" variant="soft">
          Type at least 2 characters to search the live catalog.
        </Alert>
      ) : isError ? (
        <Alert color="danger" variant="soft">
          {(error as any)?.responseData?.error || (error as any)?.response?.data?.error || 'Catalog search failed — try again.'}
          <Button size="sm" variant="plain" onClick={() => void refetch()}>Retry catalog</Button>
        </Alert>
      ) : products.length === 0 && !isFetching ? (
        <Alert color="neutral" variant="soft">
          No items matched "{debouncedTerm}".
        </Alert>
      ) : (
        <Box
          sx={
            fills
              ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
              : undefined
          }
        >
          <Box sx={{ ...(fills ? { flex: 1, minHeight: 0 } : { maxHeight: resultsHeight }), overflowY: 'auto' }}>
            <ResponsiveTable<CatalogProduct>
              rows={products}
              getRowKey={(product) => product.item_number}
              columns={columns}
              rowActions={(product) => (
                <IconButton
                  size="sm"
                  color="primary"
                  variant="soft"
                  disabled={disabled}
                  onClick={() => onAdd(product)}
                  aria-label={`Add ${product.item_number} to cart`}
                >
                  <AddIcon />
                </IconButton>
              )}
              actionsWidth="72px"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography level="body-xs" color="neutral">
              {totalItems} result{totalItems === 1 ? '' : 's'}
            </Typography>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <Typography level="body-xs">
                  {page} / {totalPages}
                </Typography>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
